import type { ResolvedCardTheme } from './cardTheme';
import type { CardPrintField } from './cardLayout';
import { isOverlayOnlyField } from './builtinFields';
import { buildFieldDisplayText } from './fieldText';
import type { CardPrintPost } from './useCardPrintPosts';

/**
 * PowerPoint (16:9 / 1 投稿 1 スライド) 書き出しの「純レイアウト」定義。
 *
 * pptxgenjs (約 376KB) に依存しないので、常時ロードされる管理画面バンドルに含めても安全。
 * これを `buildCardPptx.ts` (pptx 生成、click 時に動的 import) と `SlidePreview.tsx`
 * (画面プレビュー、常時ロード) の両方が import し、出力とプレビューの見た目を一致させる。
 *
 * 座標系は inch (pptxgenjs の既定単位)。プレビューは inch × `IN_TO_PX` で CSS px に変換する。
 */

/** 16:9 ワイド (PowerPoint 標準) のスライド寸法 (inch)。 */
export const SLIDE_W_IN = 13.333;
export const SLIDE_H_IN = 7.5;
/** inch → CSS px (96dpi)。プレビューの座標変換に使う。 */
export const IN_TO_PX = 96;
export const MM_PER_IN = 25.4;

/** スライド外周の余白 (inch)。 */
export const MARGIN_IN = 0.4;
/** 写真とテキスト領域の間隔 (inch)。 */
export const GAP_IN = 0.35;
/** 写真領域がスライド幅に占める割合。 */
export const PHOTO_WIDTH_RATIO = 0.44;
/**
 * カード (pt) → スライド (pt) のフォント拡大率。カードは 55mm 高に対しスライドは約 190mm 高で
 * 面積が大きいため、同じ pt では小さすぎる。既定タイトル 10pt→24pt / 本文 8pt→約19pt 相当。
 */
export const SLIDE_FONT_SCALE = 2.4;
/** QR コードの一辺 (inch)。カードの mm 指定より読み取りやすいよう固定サイズに拡大。 */
export const QR_SIZE_IN = 1.15;
/** ロゴ領域の寸法 (inch)。 */
export const LOGO_W_IN = 1.7;
export const LOGO_H_IN = 0.85;

export interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** #RGB / #RRGGBB → pptxgenjs 用の 'RRGGBB' (# なし・大文字)。不正値は fallback。 */
export function hexToPptxColor(hex: string | undefined, fallback = '000000'): string {
  if (!hex) return fallback;
  let h = hex.trim().replace(/^#/, '');
  if (h.length === 3) {
    h = h
      .split('')
      .map((c) => c + c)
      .join('');
  }
  return /^[0-9a-fA-F]{6}$/.test(h) ? h.toUpperCase() : fallback;
}

/** 'RRGGBB' (# なし) → CSS の '#RRGGBB' (プレビュー用)。 */
export function pptxColorToCss(color: string): string {
  return `#${color}`;
}

/** テーマのフォント id を PowerPoint で日本語表示できる標準フェイスにマップ (未インストール時は自動代替)。 */
export function pptxFontFace(fontFamily: string | undefined): string {
  const f = (fontFamily ?? '').toLowerCase();
  if (f.includes('serif') || f.includes('mincho') || f.includes('明朝')) return 'Yu Mincho';
  if (f.includes('mono') || f.includes('gothic-mono')) return 'MS Gothic';
  return 'Yu Gothic';
}

/** 写真領域の矩形 (inch)。photoSide に応じ左右へ配置。 */
export function computePhotoBox(theme: ResolvedCardTheme): Box {
  const w = SLIDE_W_IN * PHOTO_WIDTH_RATIO;
  const h = SLIDE_H_IN - MARGIN_IN * 2;
  const x = theme.layout.photoSide === 'left' ? MARGIN_IN : SLIDE_W_IN - MARGIN_IN - w;
  return { x, y: MARGIN_IN, w, h };
}

/** テキスト領域の矩形 (inch)。QR がテキスト側下部と重なる場合は高さを詰める。 */
export function computeTextBox(theme: ResolvedCardTheme, hasQr: boolean): Box {
  const photo = computePhotoBox(theme);
  const photoOnLeft = theme.layout.photoSide === 'left';
  const x = photoOnLeft ? photo.x + photo.w + GAP_IN : MARGIN_IN;
  const w = SLIDE_W_IN - photo.w - GAP_IN - MARGIN_IN * 2;
  let h = SLIDE_H_IN - MARGIN_IN * 2;
  // QR は右下。テキスト領域が右側 (photoSide=left) のときは QR と重なるため下を詰める。
  if (hasQr && photoOnLeft) h -= QR_SIZE_IN + GAP_IN;
  return { x, y: MARGIN_IN, w, h };
}

/** QR コードの矩形 (inch、右下)。 */
export function computeQrBox(): Box {
  return {
    x: SLIDE_W_IN - MARGIN_IN - QR_SIZE_IN,
    y: SLIDE_H_IN - MARGIN_IN - QR_SIZE_IN,
    w: QR_SIZE_IN,
    h: QR_SIZE_IN,
  };
}

/** ロゴの矩形 (inch、左下)。 */
export function computeLogoBox(): Box {
  return { x: MARGIN_IN, y: SLIDE_H_IN - MARGIN_IN - LOGO_H_IN, w: LOGO_W_IN, h: LOGO_H_IN };
}

/** カテゴリバッジのスライド用フォントサイズ (pt)。 */
export function slideBadgeFontPt(badgeFontSize: number): number {
  return Math.round(badgeFontSize * SLIDE_FONT_SCALE * 10) / 10;
}

/** カテゴリバッジの矩形 (inch、写真領域の左上)。幅はラベル文字数から概算 (fit=shrink 併用でクリップ防止)。 */
export function computeBadgeBox(theme: ResolvedCardTheme, label: string, badgeFontPt: number): Box {
  const photo = computePhotoBox(theme);
  const w = Math.min(photo.w, Array.from(label).length * (badgeFontPt / 72) * 1.1 + 0.35);
  const h = (badgeFontPt / 72) * 1.7 + 0.1;
  return { x: photo.x + 0.12, y: photo.y + 0.12, w, h };
}

export interface SlideFieldRun {
  text: string;
  fontSize: number;
  bold: boolean;
  /** 'RRGGBB' (# なし)。プレビューは pptxColorToCss で '#' を付ける。 */
  color: string;
  isTitle: boolean;
}

/** テキストボックスに流し込む段落 (run) の配列を構築する pure helper。 */
export function buildFieldRuns(
  post: CardPrintPost,
  fields: readonly CardPrintField[],
  theme: ResolvedCardTheme,
  ctx: {
    adminNote?: string;
    labelMap?: Record<string, string>;
    codeToName?: Record<string, string>;
    sessionNameMap?: Record<string, string>;
  },
): SlideFieldRun[] {
  const runs: SlideFieldRun[] = [];
  for (const f of fields) {
    if (isOverlayOnlyField(f)) continue; // category=バッジ / qrCode=QR で別途描画
    const text = buildFieldDisplayText(post, f, {
      adminNote: ctx.adminNote ?? '',
      labelMap: ctx.labelMap ?? {},
      codeToName: ctx.codeToName ?? {},
      sessionNameMap: ctx.sessionNameMap ?? {},
    });
    if (!text) continue; // スライドは面付け整列が不要なので空項目は詰める
    const isTitle = f.key === 'title';
    const basePt =
      f.style?.fontSize ?? (isTitle ? theme.layout.titleFontSize_pt : theme.layout.bodyFontSize_pt);
    runs.push({
      text,
      fontSize: Math.round(basePt * SLIDE_FONT_SCALE * 10) / 10,
      bold: !!f.style?.bold || isTitle,
      color: hexToPptxColor(f.style?.color ?? (isTitle ? theme.textHeading : theme.textColor)),
      isTitle,
    });
  }
  return runs;
}
